import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, ReceiptEuro, Sparkles, Shirt, Package, Wrench, CheckSquare, Home, Users, FileText, FolderOpen, LogOut, Plus, Menu, X, Grid, TrendingUp, Settings, Star, FileSignature } from "lucide-react";
import QuickAdd from "@/components/QuickAdd";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/analisi-economica", label: "Analisi economica", icon: TrendingUp, testid: "nav-analisi", roles: ["admin", "collaboratore"] },
  { to: "/incassi-pagamenti", label: "Incassi & Pagamenti", icon: ReceiptEuro, testid: "nav-incassi", roles: ["admin", "collaboratore"] },
  { to: "/rendiconti", label: "Rendiconti", icon: FileText, testid: "nav-rendiconti", roles: ["admin", "collaboratore"] },
  { to: "/pulizie", label: "Pulizie", icon: Sparkles, testid: "nav-pulizie" },
  { to: "/lavanderia", label: "Lavanderia", icon: Shirt, testid: "nav-lavanderia" },
  { to: "/consumabili", label: "Consumabili", icon: Package, testid: "nav-consumabili" },
  { to: "/manutenzioni", label: "Manutenzioni", icon: Wrench, testid: "nav-manutenzioni" },
  { to: "/todo", label: "Todo", icon: CheckSquare, testid: "nav-todo" },
  { to: "/appartamenti", label: "Appartamenti", icon: Home, testid: "nav-appartamenti" },
  { to: "/proprietari", label: "Proprietari", icon: Users, testid: "nav-proprietari", roles: ["admin"] },
  { to: "/documenti", label: "Documenti", icon: FolderOpen, testid: "nav-documenti" },
  { to: "/recensioni", label: "Recensioni", icon: Star, testid: "nav-recensioni" },
  { to: "/preventivi", label: "Preventivi", icon: FileSignature, testid: "nav-preventivi", roles: ["admin", "collaboratore"] },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-impostazioni", roles: ["admin"] },
];

const LOGO = "/brand/puravida-logo.png";

const BOTTOM = ["/", "/incassi-pagamenti", "/rendiconti", "/pulizie"];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [fabOpen, setFabOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  const items = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));
  const bottomItems = items.filter((i) => BOTTOM.includes(i.to));

  const NavItem = ({ item, mobile }) => (
    <NavLink to={item.to} end={item.to === "/"} data-testid={item.testid}
      className={({ isActive }) =>
        mobile
          ? `flex flex-col items-center gap-0.5 py-2 px-1 rounded-lg text-[10px] min-h-[44px] justify-center ${isActive ? "text-[var(--teal-deep)] font-semibold" : "text-slate-500"}`
          : `flex items-center gap-3 px-4 py-2 rounded-xl text-sm transition-colors ${isActive ? "bg-white/15 text-white font-semibold" : "text-white/70 hover:text-white hover:bg-white/10"}`
      }>
      <item.icon className={mobile ? "h-5 w-5" : "h-4 w-4 shrink-0"} />
      <span className={mobile ? "" : "truncate"}>{item.label}</span>
    </NavLink>
  );

  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-[var(--bg-main)]" data-testid="app-layout">
      <aside className="hidden md:flex flex-col fixed left-0 top-0 bottom-0 w-64 text-white z-30 p-4"
        style={{ background: "linear-gradient(180deg,#2E5C60 0%,#1F4245 100%)" }}>
        <div className="px-1 pb-3">
          <img src={LOGO} alt="PuraVida Home" className="w-full rounded-xl shadow-lg" style={{ maxWidth: 118 }} />
          <div className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-[color:var(--pv-mint-soft)] font-semibold">PuraVida Control</div>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto space-y-0.5 pr-1 pv-scroll">{items.map((i) => <NavItem key={i.to} item={i} />)}</nav>
        <div className="border-t border-white/15 pt-3 mt-3 shrink-0">
          <div className="px-2 pb-2">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-[color:var(--pv-mint-soft)] capitalize">{user?.role}</div>
          </div>
          <button onClick={doLogout} data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors">
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </aside>

      <header className="md:hidden sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 py-2.5 flex items-center justify-between">
        <button data-testid="mobile-menu-btn" onClick={() => setDrawerOpen(true)} className="p-2 -ml-2 rounded-lg text-slate-600"><Menu className="h-5 w-5" /></button>
        <div className="flex items-center gap-2 min-w-0">
          <img src={LOGO} alt="PuraVida Home" className="h-8 w-auto rounded-md" />
          <span className="font-display text-sm font-extrabold text-[var(--teal-deep)] truncate">PuraVida Control</span>
        </div>
        <div className="w-9" />
      </header>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={() => setDrawerOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute left-0 top-0 bottom-0 w-72 text-white p-4 flex flex-col"
            style={{ background: "linear-gradient(180deg,#2E5C60 0%,#1F4245 100%)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between pb-3 gap-2 shrink-0">
              <div className="min-w-0">
                <img src={LOGO} alt="PuraVida Home" className="w-full rounded-xl" style={{ maxWidth: 112 }} />
                <div className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-[color:var(--pv-mint-soft)] font-semibold">PuraVida Control</div>
              </div>
              <button onClick={() => setDrawerOpen(false)} className="p-2 text-white/70 shrink-0"><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex-1 min-h-0 space-y-0.5 overflow-y-auto pv-scroll">{items.map((i) => <NavItem key={i.to} item={i} />)}</nav>
            <div className="border-t border-white/15 pt-4 shrink-0">
              <div className="px-2 pb-2">
                <div className="text-sm font-medium">{user?.name}</div>
                <div className="text-xs text-[color:var(--pv-mint-soft)] capitalize">{user?.role}</div>
              </div>
              <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-white/80 hover:text-white hover:bg-white/10">
                <LogOut className="h-4 w-4" /> Esci
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="md:ml-64 pb-24 md:pb-8" data-testid="app-main">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
          <Outlet />
        </div>
      </main>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-md border-t border-slate-200 px-2 py-1.5 grid grid-cols-5 gap-1">
        {bottomItems.map((i) => <NavItem key={i.to} item={i} mobile />)}
        <button data-testid="nav-altro" onClick={() => setDrawerOpen(true)}
          className="flex flex-col items-center gap-0.5 py-2 px-1 rounded-lg text-[10px] text-slate-500 min-h-[44px] justify-center">
          <Grid className="h-5 w-5" /><span>Altro</span>
        </button>
      </nav>

      <button
        data-testid="global-new-btn"
        onClick={() => setFabOpen(true)}
        className="fixed bottom-20 right-4 md:bottom-8 md:right-8 z-40 shadow-2xl rounded-full bg-[var(--terracotta)] hover:brightness-110 text-white font-bold pl-4 pr-5 py-3.5 flex items-center gap-2 text-sm sm:text-base transition-transform hover:scale-105 active:scale-95"
      >
        <Plus className="h-5 w-5" /> Nuovo
      </button>

      <QuickAdd open={fabOpen} onOpenChange={setFabOpen} />
    </div>
  );
}
